import React, { useState, useEffect, useRef } from 'react';
import { FaFileUpload, FaFilePdf, FaTrash, FaDownload, FaPlus, FaFolder } from 'react-icons/fa';
import Modal from '../../components/ui/Modal';

const ManajemenMateri = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState(null);
  const fileInputRef = useRef(null);

  // Load materials from JSON file
  const loadMaterials = async () => {
    try {
      const response = await fetch('/materials/materials.json');
      if (response.ok) {
        const data = await response.json();
        setMaterials(data);
      } else {
        setMaterials([]);
      }
    } catch (err) {
      console.error('Error loading materials:', err);
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMaterials();
  }, []);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      setError('Hanya file PDF yang diperbolehkan');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Ukuran file maksimal 10MB');
      return;
    }

    setIsUploading(true);
    setError('');
    setSuccess('');

    try {
      // Create unique filename
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', fileName);
      formData.append('originalName', file.name);

      // Since we can't directly write to public folder from React,
      // we'll simulate the upload by creating a material entry
      // In a real implementation, you'd need a backend API
      const newMaterial = {
        id: timestamp,
        fileName: fileName,
        originalName: file.name,
        uploadDate: new Date().toISOString(),
        size: file.size
      };

      // Update materials list
      const updatedMaterials = [...materials, newMaterial];
      setMaterials(updatedMaterials);

      // Note: In a real implementation, you would:
      // 1. Send the file to a backend API
      // 2. The backend would save the file to public/materials/
      // 3. The backend would update materials.json
      
      setSuccess(`File "${file.name}" berhasil diupload`);
      
      // Reset file input
      event.target.value = '';
      
    } catch (err) {
      setError('Gagal mengupload file. Silakan coba lagi.');
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle delete material
  const handleDeleteMaterial = async (material) => {
    try {
      // Remove from materials list
      const updatedMaterials = materials.filter(m => m.id !== material.id);
      setMaterials(updatedMaterials);

      // Note: In a real implementation, you would:
      // 1. Send delete request to backend API
      // 2. Backend would delete the file from public/materials/
      // 3. Backend would update materials.json

      setSuccess(`Materi "${material.originalName}" berhasil dihapus`);
      setShowDeleteModal(false);
      setMaterialToDelete(null);
    } catch (err) {
      setError('Gagal menghapus materi. Silakan coba lagi.');
      console.error('Delete error:', err);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-3">
          <FaFolder className="text-amber-500" />
          Manajemen Materi Mitra
        </h1>
        <p className="text-gray-600">Kelola materi PDF untuk mitra</p>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Upload Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
        <h2 className="text-xl font-bold text-gray-700 mb-4 flex items-center gap-2">
          <FaFileUpload className="text-blue-500" />
          Upload Materi Baru
        </h2>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
            disabled={isUploading}
            ref={fileInputRef}
            className="hidden"
          />
          
          <FaFilePdf className="mx-auto h-12 w-12 text-red-500 mb-4" />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded-lg transition-colors"
          >
            <FaPlus />
            {isUploading ? 'Mengupload...' : 'Pilih File PDF'}
          </button>
          
          <p className="text-gray-500 text-sm mt-2">
            Maksimal 10MB • Hanya file PDF
          </p>
        </div>
      </div>

      {/* Materials List */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-bold text-gray-700 mb-4">
          Daftar Materi ({materials.length})
        </h2>

        {materials.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FaFolder className="mx-auto h-12 w-12 mb-4 opacity-50" />
            <p>Belum ada materi yang diupload</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nama File
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ukuran
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tanggal Upload
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {materials.map((material) => (
                  <tr key={material.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FaFilePdf className="text-red-500 mr-3" />
                        <span className="text-sm font-medium text-gray-900">
                          {material.originalName}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatFileSize(material.size)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(material.uploadDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <a
                          href={`/materials/${material.fileName}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                        >
                          <FaDownload />
                          Download
                        </a>
                        <button
                          onClick={() => {
                            setMaterialToDelete(material);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900 flex items-center gap-1 ml-4"
                        >
                          <FaTrash />
                          Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && materialToDelete && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setMaterialToDelete(null);
          }}
          onConfirm={() => handleDeleteMaterial(materialToDelete)}
          title="Konfirmasi Hapus"
        >
          Apakah Anda yakin ingin menghapus materi "{materialToDelete.originalName}"? Tindakan ini tidak dapat dibatalkan.
        </Modal>
      )}
    </div>
  );
};

export default ManajemenMateri;
